import {
  createEffect,
  createMemo,
  createSignal,
  Show,
  createSelector,
  For,
  onCleanup,
  onMount,
} from "solid-js";
import { useLocation, useNavigate } from "@solidjs/router";
import { View, Text, ElementNode, hexColor } from "@lightningtv/solid";
import { clientBranding } from "../config/clientConfig";
import { useFocusManager, useAnnouncer, useMouse } from "@lightningtv/solid/primitives";
import CryptoJS from "crypto-js";
import axios from "axios";
import SplashScreen from "./splashScreen/SplashScreen";

declare module "@lightningtv/solid/primitives" {
  interface KeyMap {
    Announcer: (string | number)[];
    Menu: (string | number)[];
    Backspace: (string | number)[];
  }
}

declare global {
  interface Window {
    APP: ElementNode;
  }
}

// ── Network detection hook ─────────────────────────────────────────
const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = createSignal(true);
  const [isChecking, setIsChecking] = createSignal(false);

  // ── Single HTTP probe ────────────────────────────────────────
  // Returns:
  //   true  — got any HTTP response  → network is working
  //   false — TypeError              → definitely no network
  //   null  — timeout / AbortError   → inconclusive, ignore
  //                   work on web
  // We probe our own backend so regional blocks on gstatic etc.
  // don't cause false negatives.
  // const probe = async (): Promise<boolean | null> => {
  //   try {
  //     await fetch(
  //       "https://innplay-apps-backend-229197090060.europe-west1.run.app/player_api.php",
  //       { method: "HEAD", cache: "no-store", signal: AbortSignal.timeout(5000) }
  //     );
  //     return true;
  //   } catch (e) {
  //     if (e instanceof TypeError) return false;
  //     return null; // timeout / abort — don't change state
  //   }
  // };

  //works on android
  const probe = async (): Promise<boolean | null> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      // mode:'no-cors' skips CORS preflight entirely — resolves with an opaque
      // response when network is reachable, throws TypeError when it isn't.
      // This avoids false-negatives from CORS misconfigurations on the server.
      await fetch(
        "https://innplay-apps-backend-229197090060.europe-west1.run.app/",
        { method: "GET", mode: "no-cors", cache: "no-store", signal: controller.signal }
      );
      return true;
    } catch (e: any) {
      if (e?.name === "AbortError") return null; // timeout — inconclusive
      if (e instanceof TypeError) return false;  // network unreachable
      return null;
    } finally {
      clearTimeout(timer);
    }
  };

  // ── Core logic ───────────────────────────────────────────────
  // Only called from the 10 s poll and from the Kotlin bridge.
  // Events (online / offline) are intentionally NOT used to set
  // state directly — on real Android TV hardware those events fire
  // spuriously during band-switching, DHCP renewal, and multi-NIC
  // changes, causing false "No Internet" screens.
  const verifyConnectivity = async () => {
    if (isChecking()) return;
    setIsChecking(true);
    try {
      const result = await probe();
      if (result === true) setIsOnline(true);
      if (result === false) setIsOnline(false);
      // null → inconclusive, leave current state untouched
    } finally {
      setIsChecking(false);
    }
  };

  onMount(() => {
    // First probe: delay 5 s so the WebView network stack is ready.
    // On real Android TV devices the stack isn't warmed up at JS mount
    // time and the first fetch throws TypeError even with working internet.
    // Older/slower devices need more time than emulators.
    setTimeout(verifyConnectivity, 5000);

    // ── Polling: sole mechanism for detecting loss ───────────────
    // Poll every 10 s. This is the ONLY thing that sets isOnline(false).
    // 10 s means worst case the overlay appears 10 s after real loss —
    // acceptable, and vastly better than false positives every few minutes.
    const pollId = setInterval(verifyConnectivity, 10_000);

    // ── Events: only used to trigger a faster poll on reconnect ──
    // 'online' and '__networkRestored' (Kotlin bridge) both just
    // call verifyConnectivity() — they do NOT set state themselves.
    // This means a real reconnect is confirmed within ~5 s (probe
    // timeout) rather than waiting up to 10 s for the next poll.
    //
    // 'offline' is deliberately ignored — it fires too spuriously on
    // real Android TV firmware to be trusted for state changes.
    // The 10 s poll will catch real loss soon enough.
    const handleOnline = () => {
      console.log("🌐 'online' event — triggering early probe");
      verifyConnectivity();
    };

    window.addEventListener("online", handleOnline);

    (window as any).__networkRestored = () => {
      console.log("🌐 __networkRestored from Kotlin — triggering early probe");
      verifyConnectivity();
    };

    onCleanup(() => {
      window.removeEventListener("online", handleOnline);
      delete (window as any).__networkRestored;
      clearInterval(pollId);
    });
  });

  return { isOnline, isChecking };
};

// ── App ────────────────────────────────────────────────────────────
const App = (props) => {
  const [isSplashVisible, setSplashVisible] = createSignal(true);
  const [useRCU, setUseRCU] = createSignal(false);

  const { isOnline, isChecking } = useNetworkStatus();

  // ── Dispatch "connectivity-restored" exactly when isOnline flips true ──
  // LIVETV listens for this (not the raw 'online' event) so it only
  // reloads the stream after connectivity has been *verified*, not just
  // signalled by the OS — avoiding a race against a still-unready network.
  let wasOnlinePrev = true;
  createEffect(() => {
    const online = isOnline();
    if (online && !wasOnlinePrev) {
      window.dispatchEvent(new CustomEvent("connectivity-restored"));
    }
    wasOnlinePrev = online;
  });

  const keyy = createMemo(() => (useRCU() ? "RCUBack" : "GoBack"));

  createEffect(() => {
    console.log("Escape key mapped to:", keyy());
    useFocusManager({
      Announcer: ["a"],
      Menu: ["m"],
      Escape: ["Escape", 27],
      Backspace: ["Backspace", 8],
      Left: ["ArrowLeft", 37],
      Right: ["ArrowRight", 39],
      Up: ["ArrowUp", 38],
      Down: ["ArrowDown", 40],
      Enter: ["Enter", 13],
    });
  });

  // ── Block ALL key events at document level when offline ────────────
  // This runs in capture phase — before LightningTV's focus manager
  // ever sees the event — so background focus cannot move at all.
  onMount(() => {
    const blockKeys = (e: KeyboardEvent) => {
      if (!isOnline()) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    // capture: true — fires before any bubble-phase listeners (including LightningTV)
    document.addEventListener("keydown", blockKeys, { capture: true });
    onCleanup(() => {
      document.removeEventListener("keydown", blockKeys, { capture: true });
    });
  });

  const navigate = useNavigate();
  const announcer = useAnnouncer();
  announcer.debug = false;
  announcer.enabled = false;
  const location = useLocation();

  // Handle splash screen timeout
  createEffect(() => {
    const splashTimer = setTimeout(() => {
      console.log("⏰ Splash timeout - hiding splash screen");
      setSplashVisible(false);

      setTimeout(() => {
        console.log("🧹 Post-splash cleanup check");
        const videos = document.querySelectorAll("video");
        if (videos.length > 0) {
          console.warn(`⚠️ Found ${videos.length} video elements after splash, removing...`);
          videos.forEach((video) => {
            video.pause();
            video.src = "";
            if (video.parentNode) {
              video.parentNode.removeChild(video);
            }
          });
          console.log("✅ All video elements removed");
        }
      }, 300);
    }, 3500);

    onCleanup(() => {
      clearTimeout(splashTimer);
    });
  });

  return (
    <>
      {/*
        ── No-internet overlay ──────────────────────────────────────────
        Purely visual — no focusable elements at all.
        Key blocking is handled at document capture level above,
        so nothing reaches LightningTV's focus manager while offline.
      */}
      <Show when={!isOnline()}>
        <View
          width={1920}
          height={1080}
          color={hexColor("#000000")}
          alpha={0.92}
          zIndex={9999}
          skipFocus
        >
          <Text
            x={910}
            y={380}
            fontSize={72}
            color={hexColor(clientBranding().primaryColor)}
            skipFocus
          >
            ⚠
          </Text>

          <Text
            x={730}
            y={490}
            fontSize={52}
            color={hexColor("#ffffff")}
            skipFocus
          >
            No Internet Connection
          </Text>

          <Text
            x={660}
            y={570}
            fontSize={28}
            color={hexColor("#888888")}
            skipFocus
          >
            Please check your network. Reconnecting automatically...
          </Text>

          <Text
            x={850}
            y={670}
            fontSize={28}
            color={hexColor(clientBranding().primaryColor)}
            skipFocus
          >
            {isChecking() ? "Checking..." : "Waiting for connection"}
          </Text>
        </View>
      </Show>

      {/* ── Splash / main app ── */}
      <Show
        when={isSplashVisible()}
        fallback={
          <View
            ref={window.APP}
            onAnnouncer={() => (announcer.enabled = !announcer.enabled)}
            onLast={() => history.back()}
          >
            <View />
            {props.children}
          </View>
        }
      >
        <View color={hexColor("#000000")} style={{ width: "100vw", height: "100vh" }}>
          <SplashScreen />
        </View>
      </Show>
    </>
  );
};

export default App;